import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ImageLibrary = ({ images, selectedImage, onImageSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  const imageTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'intraoral', label: 'Intraoral Photos' },
    { value: 'xray', label: 'X-rays' },
    { value: 'ct', label: 'Cone Beam CT' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'reviewed', label: 'Reviewed' }
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'tooth', label: 'By Tooth Number' },
    { value: 'type', label: 'By Type' }
  ];

  // Filter and sort images
  const filteredImages = images
    ?.filter(img => {
      const matchesSearch = 
        img?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        img?.toothNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        img?.notes?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesType = filterType === 'all' || img?.type === filterType;
      const matchesStatus = filterStatus === 'all' || img?.status === filterStatus;
      
      return matchesSearch && matchesType && matchesStatus;
    })
    ?.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b?.date) - new Date(a?.date);
        case 'date_asc':
          return new Date(a?.date) - new Date(b?.date);
        case 'tooth':
          return a?.toothNumber?.localeCompare(b?.toothNumber);
        case 'type':
          return a?.type?.localeCompare(b?.type);
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_review':
        return 'text-warning bg-warning/10';
      case 'reviewed':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'intraoral':
        return 'Camera';
      case 'xray':
        return 'Zap';
      case 'ct':
        return 'Layers';
      default:
        return 'Image';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
          <Icon name="FolderOpen" size={16} />
          Image Library
        </h3>

        {/* Search */}
        <Input
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
      </div>
      {/* Filters */}
      <div className="p-4 border-b border-border space-y-3 bg-muted/30">
        <Select
          placeholder="Filter by type"
          value={filterType}
          onChange={setFilterType}
          options={imageTypes}
        />
        <Select
          placeholder="Filter by status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={statusOptions}
        />
        <Select
          placeholder="Sort by"
          value={sortBy}
          onChange={setSortBy}
          options={sortOptions}
        />
      </div>
      {/* Image List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {filteredImages?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Image" size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No images found</p>
            </div>
          ) : (
            filteredImages?.map((image) => (
              <div
                key={image?.id}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedImage?.id === image?.id
                    ? 'border-primary bg-primary/5 shadow-elevation-2'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
                onClick={() => onImageSelect(image)}
              >
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 flex-shrink-0 bg-muted rounded overflow-hidden">
                    <AppImage
                      src={image?.thumbnail}
                      alt={image?.thumbnailAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={getTypeIcon(image?.type)}
                          size={14}
                          className="text-primary"
                        />
                        <span className="text-xs font-medium text-foreground truncate">
                          {image?.category}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="MapPin" size={12} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {image?.toothNumber}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Calendar" size={12} />
                        <span>{image?.date}</span>
                      </div>
                      
                      <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${getStatusColor(image?.status)}`}>
                        {image?.status === 'pending_review' && (
                          <Icon name="Clock" size={10} />
                        )}
                        {image?.status === 'reviewed' && (
                          <Icon name="CheckCircle" size={10} />
                        )}
                        <span className="capitalize">
                          {image?.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    {image?.annotations > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-primary">
                        <Icon name="MessageSquare" size={12} />
                        <span>{image?.annotations} annotations</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Summary */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="text-xs text-muted-foreground text-center">
          Showing {filteredImages?.length} of {images?.length} images
        </div>
      </div>
    </div>
  );
};

export default ImageLibrary;